import { useClockOn } from "../useClockOn";
import { BarChart } from "../charts/BarChart";
import type { PointSelection } from "../charts/pointSelection";
import { WagmiLineChart } from "../charts/WagmiLineChart";
import type { UiTheme } from "../theme";
import { TierOneCompareRow } from "./TierOneCompareRow";

// The chart of one Tier-1 trend row, split out of `TierOneTrendRow.tsx`
// (PG-63, the 500-line cap): the e1RM line with its compare line, or the
// reps-at-load bars. Both read by a tap into the row's `selection`, which
// indexes `chart.points` (PG-64 fetta 3); the compared line is never read.

// Not exported: both panels build these inline in the `chart` prop, so the
// name has no callsite outside this file (knip, 2026-07-25).
type TierOneTrendChartPoint = { x: number; y: number; label: string };

export type TierOneChartSpec = {
  key: string;
  points: TierOneTrendChartPoint[];
  yFormat: (n: number) => string;
  /**
   * The value in the row's header (and in each bar's spoken name), e.g.
   * "13 × 40.5 kg". `yFormat` stays the number printed on a bar and the
   * line's a11y value. Absent = `yFormat`.
   */
  readoutFormat?: (n: number) => string;
  /**
   * How the series is drawn. The two strength axes are different KINDS of
   * quantity and a shared row must not pretend otherwise (PG-4):
   *
   * - `"line"` (default): e1RM. A continuous estimate in kg; the line
   *   between two sessions is a legitimate reading of a trend, and the
   *   real spacing between dates carries meaning.
   * - `"bars"`: reps at a fixed load. A COUNT, taken once per session,
   *   usually 3-6 of them. A line there draws a slope through values that
   *   never existed (no one did 13.5 reps between Tuesday and Friday), and
   *   its auto-scaled y turns ±1 rep into a sawtooth. Zero-baseline bars
   *   say the honest thing (this session against that one) and cannot
   *   exaggerate a one-rep difference.
   */
  variant?: "line" | "bars";
  /**
   * PG-24: the exercise that held this pattern before the current one,
   * drawn behind it so the athlete can compare what the two movements
   * actually returned. His words (2026-09-16): *"mi mostrerebbe che alla
   * pressa si aumenta di più e più rapidamente, con le lunge si resta
   * abbastanza fermi"*.
   *
   * Absent on every row that has not changed hands, which is most of them,
   * and on the reps-at-load axis, which has no occupancy concept: those
   * render exactly as before.
   *
   * ⛔ `"bars"` ignores it: two overlaid bar series at one load would be two
   * counts fighting for the same baseline, unreadable at 48pt high. If the
   * second axis ever needs a comparison it needs its own FORM, not this one.
   */
  compare?: ReadonlyArray<{
    key: string;
    points: TierOneTrendChartPoint[];
    /**
     * Solo il nome, es. "Hack Squat Machine": va DA SOLO sulla riga sotto
     * l'headline, colorato come la propria curva. ⛔ Non opzionale: due
     * linee senza niente che nomini la seconda è un grafico illeggibile.
     *
     * ⚠️ Was a complete sentence until PG-26 (2026-09-17). It carried the
     * "vs …" / "Previously …" split, a FACT the app worked out said
     * differently from a curve he asked for by name. He replaced that with
     * one separator and the ✕ as the sole visible marker of his own choice.
     * ⛔ The distinction is NOT gone: it moved into `a11yLabel`, because a
     * screen-reader user cannot see whether a ✕ is there.
     */
    name: string;
    /** La parola fra i due nomi: "vs", e "против" in ru. ⛔ Si appende
     *  all'HEADLINE («Back Squat vs»), non alla riga sotto: il nome corrente
     *  non si ripete. Sembra punteggiatura, è una parola, quindi è del
     *  chiamante come tutto il resto. */
    separator: string;
    /** What the whole compare caption says to a screen reader, including
     *  whether this pairing is the athlete's choice or the app's. */
    a11yLabel: string;
  }>;
};

type CompareAction = { a11yLabel: string; onPress: () => void };

// Which variant each curve of a compare pairing is drawn as. ⛔ Declared
// once and handed to BOTH the chart and the legend below: they must agree,
// and a second literal is exactly how they stop agreeing.
const COMPARE_VARIANTS = { current: "focal", compared: "secondary" } as const;

export function TierOneChart({
  theme,
  name,
  chart,
  compareAction,
  compareClear,
  selection,
}: {
  theme: UiTheme;
  /** The row's own name, the focal curve's a11y name. */
  name: string;
  chart: TierOneChartSpec;
  compareAction?: CompareAction;
  compareClear?: CompareAction;
  /** The row owns it, its header reads the selected point. */
  selection: PointSelection;
}) {
  // PG-55: one clock per data load, not per render.
  const nowMs = useClockOn(chart.points);
  if (chart.variant === "bars") {
    return (
      <BarChart
        theme={theme}
        points={chart.points}
        valueFormat={chart.yFormat}
        readoutFormat={chart.readoutFormat}
        selection={selection}
      />
    );
  }
  // ⛔ ONE compared curve, the same `[0]` the row's headline reads. The
  // colour vocabulary runs out at two (`measurement-contract.md §Il vincolo
  // di COLORE`): a third would be drawn `secondary` twice.
  const compare = chart.compare?.[0];
  return (
    <>
      {/* ⛔ NO `accessible` wrapper here. It was one, and two reviewers
          caught the same regression independently: `accessible` fuses the
          subtree into ONE node, so the Chip stopped being reachable as a
          button and the chart's per-point targets went with it. The twin
          block in the row, the machine row, has no wrapper either, and
          that is the pattern. The caption names the second curve on its
          own; it does not need to be glued to the chart to do it. */}
      <TierOneCompareRow
        theme={theme}
        comparedVariant={COMPARE_VARIANTS.compared}
        compare={compare ? { name: compare.name, a11yLabel: compare.a11yLabel } : undefined}
        swap={compareAction}
        clear={compareClear}
      />
      {/* ⛔ The caption EXPLAINS, the button ACTS: his rule, and the
          reason the compare line above holds both without folding the
          name into a tappable label. */}
      <WagmiLineChart
        series={[
          // Behind first: the predecessor is context, the current
          // occupant is the reading. Drawing it second would put the
          // line the row's headline quotes UNDER the one it does not.
          // ⛔ `[0]` only, and it must match the legend's `[0]` above:
          // a curve the legend does not name is a curve nobody can read,
          // which is the whole of PG-26. See the colour-ceiling note there.
          ...(compare
            ? [
                {
                  key: compare.key,
                  variant: COMPARE_VARIANTS.compared,
                  points: compare.points,
                  // PG-53: same name the compare row (above) already
                  // shows on screen, so the a11y label and the visible
                  // legend never disagree.
                  name: compare.name,
                },
              ]
            : []),
          {
            key: chart.key,
            variant: COMPARE_VARIANTS.current,
            points: chart.points,
            // PG-31: declares this the series a tap reads (it already
            // draws last, on top; this just stops that also being an
            // implicit contract).
            headline: true,
            // PG-53: the same `name` the row's headline text already
            // shows for the focal curve.
            name,
          },
        ]}
        yFormat={chart.yFormat}
        showMeta={false}
        height={48}
        nowMs={nowMs}
        theme={theme}
        selection={selection}
      />
    </>
  );
}
